import { AuthService } from '@core/services/AuthService';
import { UserService } from '@core/services/UserService';
import { PermissionService } from '@core/services/PermissionService';
import { UserConfigService } from '@core/services/UserConfigService';
import { ModulesService } from '@core/services/ModulesService';
import { User } from '@core/models/user';
import { Lang } from '@shared/helpers/lang';
import { Currency } from '@shared/helpers/currency';
import numeral from 'numeral';
import moment from 'moment-es6';
import i18n from '@app/plugins/i18n';

export const actions = {
  login: async ({ commit, dispatch }, data) => {
    if (typeof data === 'object') {
      try {
        const { access_token } = await AuthService.login(data);
        commit('token', access_token);
      } catch (e) {

        if (e?.response?.status === 401) {
          throw Error(i18n.t('shared.errors.incorrect_login'));
        }   
      }
    }

    try {
      return await dispatch('syncFullProfile');
    } catch (e) {
      commit('token', null);

      if (e?.response?.status === 403 && e?.response?.data?.message) {
        throw Error(e?.response?.data?.message);
      } else {
        throw Error(i18n.t('shared.errors.authorisation'));
      }
    }
  },

  /**
   * Вызываем для ПОЛНОЙ синхронизации информации с бекенда.
   * К примеру получаем актуальную информацию при авторизации
   */
  syncFullProfile: async ({ dispatch, getters }) => {
    const profile_request = UserService.getItem(getters.current_user_id);
    const permission_request = PermissionService.getItem(getters.current_user_id);
    const config_request = UserConfigService.getItem(getters.current_user_id);
    const modules_request = ModulesService.getList(getters.current_user_id);

    const [user, permissions, config, modules] = await Promise.all([
      profile_request,
      permission_request,
      config_request,
      modules_request
    ]);

    return dispatch('saveFullProfileInStore', {
      user,
      config,
      permissions,
      modules
    });
  },

  syncOnlyUserProfile: async ({ dispatch, getters }) => {
    const user = await UserService.getItem(getters.current_user_id);
    return dispatch('saveUserInStore', user);
  },

  syncOnlyUserConfig: async ({ dispatch, getters }) => {
    const config = await UserConfigService.getItem(getters.current_user_id);
    return dispatch('saveConfigInStore', config);
  },
  /**
   * Сохраняем информацию полученную из syncFullProfile
   */
  saveFullProfileInStore: async ({ dispatch }, data) => {
    const user = data.user || {};
    const config = data.config || {};
    const permissions = data.permissions || {};
    const modules = data.modules || {};

    const [current_user, current_config, current_permissions, current_modules] = await Promise.all([
      dispatch('saveUserInStore', user),
      dispatch('saveConfigInStore', config),
      dispatch('savePermissionsInStore', permissions),
      dispatch('saveModulesInStore', modules)
    ]);

    return {
      user: current_user,
      config: current_config,
      permissions: current_permissions,
      modules: current_modules
    };
  },

  /**
   * Сохраняем информацию о пользователе в Store
   * Перед сохранениям можем вызвать необходимые методы,
   * К примеру установить локаль.
   */
  saveUserInStore: async ({ dispatch, getters, commit }, new_data) => {
    const user = new_data;
    const { currency, locale } = new User(user);
    commit('user', user);

    if (locale) {
      await dispatch('setFrontendLocale', locale);
    }

    if (currency) {
      await dispatch('setFrontendCurrency', currency);
    }
    return getters['user'];
  },

  setFrontendLocale: async (_, locale) => {
    const current = locale && Lang.isAllowed(locale) ? locale : Lang.getDefault();
    i18n.locale = current;
    moment.locale(current);
  },

  setFrontendCurrency: async (_, currency) => {
    const current = currency && Currency.isAllowed(currency) ? currency : Currency.getDefault();
    numeral.locale(current);
  },

  setBackendLocale: ({ dispatch, getters }, locale) => {
    const current = locale && Lang.isAllowed(locale) ? locale : Lang.getDefault();
    UserService.setLocale(getters.current_user_id, current);
    return dispatch('setFrontendLocale', current);
  },

  /**
   * Сохраняем конфи пользователя
   */
  saveConfigInStore: async ({ getters, commit }, new_data) => {
    commit('config', new_data);
    return getters['config'];
  },

  /**
   * Сохраняем разрешения пользователя
   */
  savePermissionsInStore: async ({ getters, commit }, new_data) => {
    commit('permissions', new_data);
    return getters['permissions'];
  },

  saveModulesInStore: async ({ getters, commit }, new_data) => {
    commit('modules', new_data);
    return getters['modules'];
  },

  setProfileBackendWithStore: async ({ getters, dispatch }, data) => {
    await UserService.editItem(getters.current_user_id, data);
    return dispatch('syncOnlyUserProfile');
  },

  setBackendPaymentInfo: async ({ getters, dispatch }, data) => {
    await UserService.setPaymentInfo(getters.current_user_id, data);
    return dispatch('syncOnlyUserProfile');
  },

  logout: ({ commit }) => {
    commit('token', null);
  }
};
